import type { User } from '@/api/types';
import { filtersFromSearchParams } from '@/features/catalog/filterModel';

export interface AutoStartInput {
  user: User | null;
  isAuthenticated: boolean;
  /** Сервер ответил на /me в этой сессии — иначе флаг мог прийти из устаревшего кэша. */
  isAccessResolved: boolean;
  pathname: string;
  /** Локальный флаг «пройден» — защита до ответа сервера и после неудачной отметки (ONB-3). */
  localDone: boolean;
  /** Поверх каталога уже открыт другой модальный слой (paywall с лендинга). */
  blocked: boolean;
  /**
   * Сессия страницы началась с готового запроса каталога (`/app/?q=…` — призыв статьи блога):
   * гид не перекрывает выдачу и не уводит на чистый `/`, запрос и utm-метки остаются в адресе.
   */
  enteredWithQuery: boolean;
}

/**
 * Автопоказ гида (ONB-1): авторизован, email подтверждён, серверный флаг
 * `onboarding_completed_at` строго `null` (undefined = старый ответ сервера — не показываем),
 * пользователь в каталоге и пришёл не с готовым запросом. Существующие пользователи получают
 * флаг бэкфиллом (ONB-2).
 */
export function shouldAutoStartTour(input: AutoStartInput): boolean {
  const { user, isAuthenticated, isAccessResolved, pathname, localDone, blocked } = input;
  if (!isAuthenticated || !user) return false;
  if (!user.emailVerified) return false;
  if (!isAccessResolved) return false;
  if (user.onboarding_completed_at !== null) return false;
  if (pathname !== '/') return false;
  if (localDone) return false;
  if (blocked) return false;
  if (input.enteredWithQuery) return false;
  return true;
}

/** В адресе есть запрос каталога: ключ и разбор — те же, что у самого каталога (CAT-4). */
export function hasCatalogQuery(search: string): boolean {
  return filtersFromSearchParams(new URLSearchParams(search)).query.trim() !== '';
}

export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface CoachPlacement {
  /** Рамка прожектора вокруг цели (с отступом 6px) или null — карточка по центру. */
  spot: { x: number; y: number; w: number; h: number } | null;
  tip: { x: number; y: number };
  width: number;
}

const PAD = 6;
const GAP = 16;
const MARGIN = 16;

/**
 * Раскладка коуч-марки (Onboarding.dc.html → measure): карточка под целью, при нехватке
 * места — над ней; для крупной цели (больше 60% ширины и 50% высоты) — справа сверху.
 */
export function placeCoach(
  target: Rect | null,
  viewport: { width: number; height: number },
  boxHeight = 200,
): CoachPlacement {
  const W = viewport.width;
  const H = viewport.height;
  const width = Math.min(360, W - 32);
  if (!target) {
    return {
      spot: null,
      tip: { x: (W - width) / 2, y: Math.max(24, H / 2 - boxHeight / 2) },
      width,
    };
  }
  const spot = {
    x: target.left - PAD,
    y: target.top - PAD,
    w: target.width + PAD * 2,
    h: target.height + PAD * 2,
  };
  let x = Math.min(Math.max(MARGIN, target.left), W - width - MARGIN);
  let y = target.top + target.height + GAP;
  if (y + boxHeight > H - MARGIN) y = Math.max(MARGIN, target.top - boxHeight - GAP);
  if (target.width > W * 0.6 && target.height > H * 0.5) {
    x = W - width - 24;
    y = Math.max(24, Math.min(H - boxHeight - 24, target.top + 24));
  }
  return { spot, tip: { x, y }, width };
}

/** Цель нужно докрутить, если она выше шапки или ниже безопасной зоны (прототип: 60 / 40 px). */
export function needsScroll(target: Rect, viewportHeight: number): boolean {
  return target.top + target.height > viewportHeight - 40 || target.top < 60;
}
